import React, { useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CartContext } from '../context/CartContext';
import { AuthContext } from '../context/AuthContext';
import { useToast } from '../context/ToastContext';
import { useSettings } from '../context/SettingsContext';
import { formatCurrency } from '../utils/currency';
import { FaCreditCard, FaTruck, FaMoneyBillWave, FaLock } from 'react-icons/fa';
import './Checkout.css';

const Checkout = () => {
  const { cart, getCartTotal, clearCart } = useContext(CartContext);
  const { user } = useContext(AuthContext);
  const toast = useToast();
  const { settings } = useSettings();
  const navigate = useNavigate();

  const [shippingAddress, setShippingAddress] = useState({
    name: user?.name || '',
    street: user?.address?.street || '',
    city: user?.address?.city || '',
    state: user?.address?.state || '',
    zipCode: user?.address?.zipCode || '',
    country: user?.address?.country || 'Nigeria',
    phone: user?.phone || '',
  });
  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [loading, setLoading] = useState(false);

  // Default to Paystack when the store has it enabled
  useEffect(() => {
    if (settings?.paystackEnabled) setPaymentMethod('paystack');
  }, [settings?.paystackEnabled]);

  useEffect(() => {
    if (cart.length === 0) navigate('/cart');
  }, [cart.length, navigate]);

  const subtotal = getCartTotal();
  const taxRate = (settings?.taxRate || 0) / 100;
  const tax = subtotal * taxRate;
  const freeShipThreshold = settings?.freeShippingThreshold || 0;
  const shippingFee = settings?.shippingFee || 0;
  const shipping = freeShipThreshold > 0 && subtotal >= freeShipThreshold ? 0 : shippingFee;
  const total = subtotal + tax + shipping;

  const handleChange = (e) => {
    setShippingAddress({ ...shippingAddress, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const orderData = {
        items: cart.map((item) => ({ product: item._id, quantity: item.quantity })),
        shippingAddress,
        paymentMethod,
      };

      const res = await axios.post('/api/orders', orderData);
      const order = res.data;

      // Paystack flow: initialize transaction and redirect to the gateway
      if (paymentMethod === 'paystack') {
        const callbackUrl = `${window.location.origin}/payment-callback?orderId=${order._id}`;
        const initRes = await axios.post('/api/payments/paystack/initialize', {
          orderId: order._id,
          callbackUrl,
        });
        clearCart();
        // Redirect the browser to Paystack's secure payment page
        window.location.href = initRes.data.authorizationUrl;
        return;
      }

      // Cash on delivery
      clearCart();
      toast.success('Order placed successfully');
      navigate(`/order-confirmation/${order._id}`);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Order failed. Please try again.');
      setLoading(false);
    }
  };

  if (cart.length === 0) return null;

  return (
    <div className="checkout-page page">
      <div className="container">
        <div className="checkout-header">
          <h1>Checkout</h1>
          <p className="page-subtitle">Almost there. Just a few details to complete your order.</p>
        </div>

        <form onSubmit={handleSubmit} className="checkout-grid">
          <div className="checkout-main">
            {/* Shipping */}
            <section className="checkout-section">
              <div className="section-title">
                <FaTruck />
                <h2>Shipping address</h2>
              </div>

              <div className="form-group">
                <label>Full name</label>
                <input
                  type="text" name="name" required
                  value={shippingAddress.name} onChange={handleChange}
                />
              </div>

              <div className="form-group">
                <label>Street address</label>
                <input
                  type="text" name="street" required
                  value={shippingAddress.street} onChange={handleChange}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City</label>
                  <input type="text" name="city" required value={shippingAddress.city} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>State / Province</label>
                  <input type="text" name="state" required value={shippingAddress.state} onChange={handleChange} />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>ZIP / Postal code</label>
                  <input type="text" name="zipCode" required value={shippingAddress.zipCode} onChange={handleChange} />
                </div>
                <div className="form-group">
                  <label>Country</label>
                  <input type="text" name="country" required value={shippingAddress.country} onChange={handleChange} />
                </div>
              </div>

              <div className="form-group">
                <label>Phone number</label>
                <input type="tel" name="phone" required value={shippingAddress.phone} onChange={handleChange} />
              </div>
            </section>

            {/* Payment */}
            <section className="checkout-section">
              <div className="section-title">
                <FaCreditCard />
                <h2>Payment method</h2>
              </div>

              <div className="payment-options">
                {settings?.paystackEnabled && (
                  <label className={`payment-option ${paymentMethod === 'paystack' ? 'selected' : ''}`}>
                    <input
                      type="radio" name="paymentMethod" value="paystack"
                      checked={paymentMethod === 'paystack'}
                      onChange={(e) => setPaymentMethod(e.target.value)}
                    />
                    <FaCreditCard className="payment-icon" />
                    <div>
                      <strong>Pay with Paystack</strong>
                      <small>Card, bank transfer, USSD &amp; more — secured by Paystack</small>
                    </div>
                  </label>
                )}

                <label className={`payment-option ${paymentMethod === 'cod' ? 'selected' : ''}`}>
                  <input
                    type="radio" name="paymentMethod" value="cod"
                    checked={paymentMethod === 'cod'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <FaMoneyBillWave className="payment-icon" />
                  <div>
                    <strong>Cash on delivery</strong>
                    <small>Pay when you receive your order</small>
                  </div>
                </label>
              </div>

              <div className="security-note">
                <FaLock /> Your information is encrypted and secure
              </div>
            </section>
          </div>

          {/* Summary */}
          <aside className="checkout-summary">
            <h2>Order summary</h2>

            <div className="summary-items">
              {cart.map((item) => (
                <div key={item._id} className="summary-item">
                  <div className="summary-thumb">
                    {item.images && item.images[0] ? (
                      <img src={item.images[0].url} alt={item.name} />
                    ) : (
                      <div className="no-image">—</div>
                    )}
                    <span className="qty-badge">{item.quantity}</span>
                  </div>
                  <div className="summary-item-info">
                    <span className="summary-item-name">{item.name}</span>
                    <span className="summary-item-price">
                      {formatCurrency(item.price * item.quantity, settings?.currency)}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <div className="summary-totals">
              <div className="summary-row">
                <span>Subtotal</span>
                <span>{formatCurrency(subtotal, settings?.currency)}</span>
              </div>
              {tax > 0 && (
                <div className="summary-row">
                  <span>Tax ({settings?.taxRate}%)</span>
                  <span>{formatCurrency(tax, settings?.currency)}</span>
                </div>
              )}
              <div className="summary-row">
                <span>Shipping</span>
                <span>{shipping === 0 ? <span className="free-tag">Free</span> : formatCurrency(shipping, settings?.currency)}</span>
              </div>
              <div className="summary-divider" />
              <div className="summary-row total">
                <span>Total</span>
                <span>{formatCurrency(total, settings?.currency)}</span>
              </div>
            </div>

            <button type="submit" className="btn btn-primary btn-block btn-lg" disabled={loading}>
              {loading ? 'Placing order...' : `Place order · ${formatCurrency(total, settings?.currency)}`}
            </button>
          </aside>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
